<template>
  <div
    v-if="!!nodeData"
    :id="`node-${nodeId}`"
    ref="element"
    class="node draggable"
    @mousedown.left.stop="startNodeDrag($event)"
  >
    <div
      ref="inputContainerElement"
      class="node__port_container inputs"
    >
      <AdapterNodePort
        v-for="inputPort of inputs"
        :key="inputPort.id"
        :graph-editor="graphEditor"
        :port="inputPort"
        :disabled="disabled"
      >
        <template
          #default="{
            port, portDisabled, connectionCount,
          }"
        >
          <slot
            name="port"
            :port="port"
            :port-disabled="portDisabled"
            :connection-count="connectionCount"
          />
        </template>
      </AdapterNodePort>
    </div>
    <div
      ref="slotContainerElement"
      class="node__slot_container"
    >
      <slot
        name="default"
        :node-data="(nodeData as INode<SCHEMA>)"
        :node-disabled="disabled"
        :has-node-connections="hasNodeConnections"
        :is-node-fully-connected="isNodeFullyConnected"
      />
    </div>
    <div
      ref="outputContainerElement"
      class="node__port_container outputs"
    >
      <AdapterNodePort
        v-for="outputPort of outputs"
        :key="outputPort.id"
        :graph-editor="graphEditor"
        :port="outputPort"
        :disabled="disabled"
      >
        <template
          #default="{
            port, portDisabled, connectionCount,
          }"
        >
          <slot
            name="port"
            :port="port"
            :port-disabled="portDisabled"
            :connection-count="connectionCount"
          />
        </template>
      </AdapterNodePort>
    </div>
  </div>
</template>

<script setup lang="ts" generic="SCHEMA extends INodeSchemaRegistry = INodeSchemaRegistry">
import {
  onBeforeUnmount, onMounted, ref, shallowRef, triggerRef, useTemplateRef,
} from 'vue';

import {
  GraphEditor,
} from '../core';
import {
  useDrag,
} from '../hooks';
import {
  IGraphEditorEvents, INode,
  INodeSchemaRegistry, IPort, IPosition, TEntityIdentifier,
} from '../types';

import AdapterNodePort
  from './adapter-node-port.vue';
import {
  ISlotPropsNode,
  ISlotPropsPort, IUserInterfaceAdapterProps,
} from './types.ts';

const props = defineProps<IUserInterfaceAdapterProps & {
  graphEditor: GraphEditor<SCHEMA>,
  nodeId: TEntityIdentifier,
}>();

const nodeData = ref<INode<SCHEMA>>(props.graphEditor.getNode(props.nodeId)!);

const element = useTemplateRef('element');
const inputContainerElement = useTemplateRef('inputContainerElement');
const outputContainerElement = useTemplateRef('outputContainerElement');
const slotContainerElement = useTemplateRef('slotContainerElement');

function getNodePorts(type: IPort['type']): IPort[] {
  return props.graphEditor.getNodePorts(props.nodeId, type);
}

const inputs = ref<IPort[]>(getNodePorts('input'));
const outputs = ref<IPort[]>(getNodePorts('output'));

let observerPort: ResizeObserver;
let observerNode: ResizeObserver;

function setPosition(newPosition: IPosition) {
  if (!element.value) {
    return;
  }
  // We use transform: translate instead of position: absolute + left + right,
  // as transform css is GPU accelerated and provides some significant performance.
  // It also skips the lay-outing phase of the browser and thus gains another
  // performance boost
  element.value!.style.transform = `translate(${newPosition.x}px, ${newPosition.y}px)`;
}
function onNodeMoved(event: IGraphEditorEvents['node:moved']) {
  if (event.id !== props.nodeId) {
    return;
  }
  setPosition(event.position);
}
function onNodePortsChanged(event: IGraphEditorEvents['node:portsChanged']) {
  if (event.id !== props.nodeId) {
    return;
  }
  inputs.value = getNodePorts('input');
  outputs.value = getNodePorts('output');
}

function adjustMinHeight() {
  if (!slotContainerElement.value) {
    return;
  }
  const inputHeight = inputContainerElement.value?.offsetHeight ?? 0;
  const outputHeight = outputContainerElement.value?.offsetHeight ?? 0;
  const requiredHeight = Math.max(inputHeight, outputHeight);
  slotContainerElement.value.style.minHeight = `${requiredHeight}px`;
}

function onNodeResize(domRect: DOMRectReadOnly) {
  props.graphEditor.setNodeSize(props.nodeId, domRect.width, domRect.height);
}

function handleDrag(deltaX: number, deltaY: number) {
  props.graphEditor.moveNodeScreen(props.nodeId, deltaX, deltaY);
}

const {
  start: startNodeDrag,
} = useDrag({
  onPan: handleDrag,
});

const hasNodeConnections = shallowRef(false);
const isNodeFullyConnected = shallowRef(false);

function checkConnectionStatus() {
  hasNodeConnections.value = props.graphEditor.isNodeConnected(props.nodeId);
  isNodeFullyConnected.value = props.graphEditor.isNodeFullyConnected(props.nodeId);
}

function onConnectionChange(event: IGraphEditorEvents['connection:created'] | IGraphEditorEvents['connection:deleted']) {
  if (event.fromNodeId !== props.nodeId && event.toNodeId !== props.nodeId) {
    return;
  }
  checkConnectionStatus();
}

function onNodeDataChange(event: IGraphEditorEvents<SCHEMA>['node:dataChanged']) {
  if (event.id !== props.nodeId) {
    return;
  }
  nodeData.value = event;
  triggerRef(nodeData);
}

onMounted(() => {
  observerNode = new ResizeObserver((entries) => {
    onNodeResize(entries[0].contentRect);
  });
  if (element.value) {
    props.graphEditor.setNodeElement(props.nodeId, element.value);
    observerNode.observe(element.value);
  }

  observerPort = new ResizeObserver(adjustMinHeight);
  if (inputContainerElement.value) {
    observerPort.observe(inputContainerElement.value);
  }
  if (outputContainerElement.value) {
    observerPort.observe(outputContainerElement.value);
  }

  const nodeGeometry = props.graphEditor.getNodeGeometry(props.nodeId);
  setPosition({
    x: nodeGeometry?.x || 0,
    y: nodeGeometry?.y || 0,
  });

  checkConnectionStatus();

  props.graphEditor.addEventListener('node:moved', onNodeMoved);
  props.graphEditor.addEventListener('node:portsChanged', onNodePortsChanged);
  props.graphEditor.addEventListener('node:dataChanged', onNodeDataChange);
  props.graphEditor.addEventListener('connection:created', onConnectionChange);
  props.graphEditor.addEventListener('connection:deleted', onConnectionChange);
});

onBeforeUnmount(() => {
  props.graphEditor.removeEventListener('node:moved', onNodeMoved);
  props.graphEditor.removeEventListener('node:portsChanged', onNodePortsChanged);
  props.graphEditor.removeEventListener('node:dataChanged', onNodeDataChange);
  props.graphEditor.removeEventListener('connection:created', onConnectionChange);
  props.graphEditor.removeEventListener('connection:deleted', onConnectionChange);
});

defineSlots<{
  default(props: ISlotPropsNode<SCHEMA>): void,
  port(props: ISlotPropsPort): void,
}>();

</script>

<style scoped lang="scss">
@use './styles.scss';

.node {
  @include styles.draggable;

  z-index: styles.$z-level-node;
  position: absolute;
  cursor: default;

  /* Prevent the user selecting text in the example */
  user-select: none;

  &__port_container {
    z-index: styles.$z-level-node-port;
    position: absolute;
    top: 50%;
    display: flex;
    flex-direction: column;
    padding: 0.75rem 0;
    gap: 0.75rem;
    justify-content: center;

    &.outputs {
      right: 0;
      transform: translate(50%, -50%);
    }

    &.inputs {
      left: 0;
      transform: translate(-50%, -50%);
    }
  }

  // enforces that the child of the slot container fills the remaining space
  .node__slot_container {
    display: flex;
    flex-direction: column;
  }

  .node__slot_container > * {
    flex: 1 1 auto;
    min-height: 100%;
    box-sizing: border-box;
  }

}
</style>
